import { ApplianceFeatures, type EncoderSettings, MatroxPortMode, PortMode } from 'common/api/v1/types'
import GeneralEncoderDecoderSettings from '../../common/GeneralEncoderDecoderSettings'
import { useEffect } from 'react'
import { makeDefaultVaEncoderSettings } from 'common/encoderSettingsUtil'
import { EnrichedOutputPort } from '../../../api/nm-types'
import { useFormContext } from 'react-hook-form'
import { isGeneralEncoderSettings } from 'common/matrox'

function isCorrectEncoderSettingsTypeForPortMode(
  encoderSettings: EncoderSettings | undefined,
  mode: PortMode,
  applianceFeatures: ApplianceFeatures,
) {
  const isMatroxMode = mode === MatroxPortMode.matroxSdi
  const modesWithDecoderSettings = applianceFeatures.output?.modes.filter((m) => !!m.decoder).map((m) => m.mode) ?? []
  if (modesWithDecoderSettings.includes(mode) && !isMatroxMode) {
    return encoderSettings && isGeneralEncoderSettings(encoderSettings)
  }
  // Port mode has no support for encoder settings
  // or is Matrox which has its decoder settings embedded in the MatroxSdiPortForm component, presented separately
  // - should be undefined
  return encoderSettings === undefined
}

function makeEncoderSettings(mode: PortMode, applianceFeatures: ApplianceFeatures) {
  const isMatroxMode = mode === MatroxPortMode.matroxSdi
  const modesWithDecoderSettings = applianceFeatures.output?.modes.filter((m) => !!m.decoder).map((m) => m.mode) ?? []
  const hasDecoderSettings = modesWithDecoderSettings.includes(mode)
  return isMatroxMode
    ? undefined // Matrox decoder settings are embedded in the MatroxSdiPortForm component, presented separately.
    : hasDecoderSettings
      ? makeDefaultVaEncoderSettings()
      : undefined
}

interface Props {
  namePrefix: string
  applianceFeatures: ApplianceFeatures
}
const EncoderSettings = ({ namePrefix, applianceFeatures }: Props) => {
  const { getValues, setValue } = useFormContext()
  const logicalPort: EnrichedOutputPort = getValues(namePrefix)
  const decoderFeatures = applianceFeatures.output?.modes.find((m) => m.mode === logicalPort.mode)?.decoder
  const encoderSettings = (logicalPort as any).encoderSettings

  useEffect(() => {
    if (!isCorrectEncoderSettingsTypeForPortMode(encoderSettings, logicalPort.mode, applianceFeatures)) {
      const newEncoderSettings = makeEncoderSettings(logicalPort.mode, applianceFeatures)
      setValue(`${namePrefix}.encoderSettings`, newEncoderSettings)
    }
  }, [encoderSettings, logicalPort.mode, setValue, namePrefix])

  if (!decoderFeatures) {
    return null
  }
  if (!encoderSettings) {
    return null
  }
  if (!isCorrectEncoderSettingsTypeForPortMode(encoderSettings, logicalPort.mode, applianceFeatures)) {
    return null
  }

  return (
    <>
      <GeneralEncoderDecoderSettings namePrefix={`${namePrefix}.encoderSettings`} decoderFeatures={decoderFeatures} />
    </>
  )
}

export default EncoderSettings
